"use client"

import type React from "react"
import { useEffect, useRef } from "react"

type ClickEffectType = "ripple" | "burst" | "sparkle" | "shockwave" | "confetti" | "implode"

interface Particle {
  angle: number
  speedMult: number
  confettiColor: string
  rotSpeed: number
  pw: number
  ph: number
}

interface ClickEffect {
  x: number
  y: number
  startTime: number
  id: number
  particles: Particle[]
}

interface CursorClickEffectProps {
  /** Maximum diameter the effect expands to in px */
  size?: number
  /** Animation duration in ms */
  duration?: number
  /** CSS color string (ignored for confetti, use palette) */
  color?: string
  /** Stroke / particle width in px */
  strokeWidth?: number
  /** z-index */
  zIndex?: number
  /**
   * "ripple"    — expanding ring (default)
   * "burst"     — particles radiating outward
   * "sparkle"   — star-ray lines
   * "shockwave" — two concentric rings with stagger
   * "confetti"  — colored rectangles with gravity
   * "implode"   — particles rush inward then explode outward
   */
  type?: ClickEffectType
  /** Number of particles / rays */
  particleCount?: number
  /** Color palette for "confetti" type */
  palette?: string[]
}

const TAU = Math.PI * 2

const DEFAULT_PALETTE = [
  "#ff6b6b", "#ffd93d", "#6bcb77",
  "#4d96ff", "#c77dff", "#ff9a3c",
]

export const CursorClickEffect: React.FC<CursorClickEffectProps> = ({
  size = 50,
  duration = 600,
  color = "rgba(255, 255, 255, 0.8)",
  strokeWidth = 2,
  zIndex = 9997,
  type = "ripple",
  particleCount = 12,
  palette,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const effectsRef = useRef<ClickEffect[]>([])
  const idRef = useRef(0)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener("resize", resize, { passive: true })

    const effectivePalette = palette ?? DEFAULT_PALETTE

    // Animate only while there are active effects — idle-suspend between clicks.
    const animate = (time: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      effectsRef.current = effectsRef.current.filter((effect) => {
        const elapsed = Math.max(0, time - effect.startTime)
        if (elapsed >= duration) return false

        const progress = elapsed / duration

        if (type === "ripple")         drawRipple(ctx, effect, progress, size, color, strokeWidth)
        else if (type === "burst")     drawBurst(ctx, effect, progress, size, color, strokeWidth)
        else if (type === "sparkle")   drawSparkle(ctx, effect, progress, size, color, strokeWidth)
        else if (type === "shockwave") drawShockwave(ctx, effect, elapsed, duration, size, color, strokeWidth)
        else if (type === "confetti")  drawConfetti(ctx, effect, elapsed, duration, size)
        else if (type === "implode")   drawImplode(ctx, effect, progress, size, color, strokeWidth)

        ctx.globalAlpha = 1
        return true
      })

      if (effectsRef.current.length > 0) {
        rafRef.current = requestAnimationFrame(animate)
      } else {
        // All effects finished — stop the loop until the next click
        rafRef.current = null
      }
    }

    const spawnEffect = (x: number, y: number) => {
      const particles: Particle[] = Array.from({ length: particleCount }, (_, i) => ({
        angle: (i / particleCount) * TAU + (Math.random() - 0.5) * (TAU / particleCount),
        speedMult: 0.6 + Math.random() * 0.4,
        confettiColor: effectivePalette[i % effectivePalette.length],
        rotSpeed: (Math.random() - 0.5) * 18,
        pw: 8 + Math.random() * 10,
        ph: 4 + Math.random() * 6,
      }))
      effectsRef.current.push({ x, y, startTime: performance.now(), id: idRef.current++, particles })
      // Wake the RAF loop if it was idle
      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(animate)
      }
    }

    const onClick = (e: MouseEvent) => spawnEffect(e.clientX, e.clientY)
    const onCustomClick = (e: Event) => {
      const { x, y } = (e as CustomEvent<{ x: number; y: number }>).detail
      spawnEffect(x, y)
    }

    window.addEventListener("click", onClick)
    window.addEventListener("cursor:click", onCustomClick)

    return () => {
      window.removeEventListener("resize", resize)
      window.removeEventListener("click", onClick)
      window.removeEventListener("cursor:click", onCustomClick)
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [color, duration, palette, particleCount, size, strokeWidth, type])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ position: "fixed", top: 0, left: 0, pointerEvents: "none", zIndex }}
    />
  )
}

// ─── effect renderers ─────────────────────────────────────────────────────────

function drawRipple(ctx: CanvasRenderingContext2D, effect: ClickEffect, progress: number, size: number, color: string, strokeWidth: number) {
  const r = Math.max((size / 2) * (1 - Math.pow(1 - progress, 3)), 0.5)
  ctx.beginPath()
  ctx.arc(effect.x, effect.y, r, 0, TAU)
  ctx.strokeStyle = color
  ctx.globalAlpha = 1 - progress
  ctx.lineWidth = strokeWidth
  ctx.stroke()
}

function drawBurst(ctx: CanvasRenderingContext2D, effect: ClickEffect, progress: number, size: number, color: string, strokeWidth: number) {
  const eased = 1 - Math.pow(1 - progress, 2)
  ctx.fillStyle = color
  effect.particles.forEach((p) => {
    const dist = eased * (size / 2) * p.speedMult
    const x = effect.x + Math.cos(p.angle) * dist
    const y = effect.y + Math.sin(p.angle) * dist
    ctx.beginPath()
    ctx.arc(x, y, Math.max((1 - progress) * strokeWidth * 2.5, 0.5), 0, TAU)
    ctx.globalAlpha = (1 - progress) * 0.9
    ctx.fill()
  })
}

function drawSparkle(ctx: CanvasRenderingContext2D, effect: ClickEffect, progress: number, size: number, color: string, strokeWidth: number) {
  const eased = 1 - Math.pow(1 - progress, 2)
  ctx.strokeStyle = color
  ctx.lineCap = "round"
  effect.particles.forEach((p) => {
    const x1 = effect.x + Math.cos(p.angle) * eased * (size / 2) * 0.25
    const y1 = effect.y + Math.sin(p.angle) * eased * (size / 2) * 0.25
    const x2 = effect.x + Math.cos(p.angle) * eased * (size / 2) * p.speedMult
    const y2 = effect.y + Math.sin(p.angle) * eased * (size / 2) * p.speedMult
    ctx.beginPath()
    ctx.moveTo(x1, y1)
    ctx.lineTo(x2, y2)
    ctx.globalAlpha = (1 - progress) * 0.85
    ctx.lineWidth = Math.max(strokeWidth * (1 - progress * 0.5), 0.5)
    ctx.stroke()
  })
  ctx.beginPath()
  ctx.arc(effect.x, effect.y, Math.max((1 - progress) * 3, 0.5), 0, TAU)
  ctx.fillStyle = color
  ctx.globalAlpha = 1 - progress
  ctx.fill()
}

function drawShockwave(ctx: CanvasRenderingContext2D, effect: ClickEffect, elapsed: number, duration: number, size: number, color: string, strokeWidth: number) {
  const p1 = elapsed / duration
  ctx.beginPath()
  ctx.arc(effect.x, effect.y, Math.max((size / 2) * (1 - Math.pow(1 - p1, 3)), 0.5), 0, TAU)
  ctx.strokeStyle = color
  ctx.globalAlpha = (1 - p1) * 0.85
  ctx.lineWidth = strokeWidth * 1.5
  ctx.stroke()

  const DELAY = 0.15
  if (elapsed > duration * DELAY) {
    const p2 = Math.min((elapsed - duration * DELAY) / (duration * (1 - DELAY)), 1)
    ctx.beginPath()
    ctx.arc(effect.x, effect.y, Math.max((size / 2) * 0.65 * (1 - Math.pow(1 - p2, 3)), 0.5), 0, TAU)
    ctx.globalAlpha = (1 - p2) * 0.5
    ctx.lineWidth = strokeWidth
    ctx.stroke()
  }
}

function drawConfetti(ctx: CanvasRenderingContext2D, effect: ClickEffect, elapsed: number, duration: number, size: number) {
  const t = elapsed / 1000

  effect.particles.forEach((p) => {
    const speed = size * 4 + size * 3.5 * p.speedMult
    const vx0 = Math.cos(p.angle) * speed
    const vy0 = Math.sin(p.angle) * speed * 0.5 - size * 7.5

    const drag = Math.exp(-1.5 * t)
    const x = effect.x + vx0 * t * drag
    const y = effect.y + vy0 * t + 0.5 * 900 * t * t

    const alpha = Math.max(0, 1 - Math.pow(elapsed / duration, 1.5))
    const rot = p.rotSpeed * t

    ctx.save()
    ctx.translate(x, y)
    ctx.rotate(rot)
    ctx.fillStyle = p.confettiColor
    ctx.globalAlpha = alpha
    ctx.fillRect(-p.pw / 2, -p.ph / 2, p.pw, p.ph)
    ctx.restore()
  })
}

function drawImplode(ctx: CanvasRenderingContext2D, effect: ClickEffect, progress: number, size: number, color: string, strokeWidth: number) {
  const SPLIT = 0.3
  const maxDist = size / 2

  if (progress < SPLIT) {
    const p = progress / SPLIT
    const eased = p * p
    ctx.fillStyle = color
    effect.particles.forEach((part) => {
      const dist = maxDist * (1 - eased) * part.speedMult
      const x = effect.x + Math.cos(part.angle) * dist
      const y = effect.y + Math.sin(part.angle) * dist
      ctx.beginPath()
      ctx.arc(x, y, Math.max(strokeWidth * 2, 0.5), 0, TAU)
      ctx.globalAlpha = 0.7 + eased * 0.3
      ctx.fill()
    })
    ctx.beginPath()
    ctx.arc(effect.x, effect.y, Math.max(eased * 6, 0.5), 0, TAU)
    ctx.globalAlpha = eased
    ctx.fill()
  } else {
    const p = (progress - SPLIT) / (1 - SPLIT)
    const eased = 1 - Math.pow(1 - p, 2)
    ctx.fillStyle = color
    effect.particles.forEach((part) => {
      const dist = eased * maxDist * part.speedMult
      const x = effect.x + Math.cos(part.angle) * dist
      const y = effect.y + Math.sin(part.angle) * dist
      ctx.beginPath()
      ctx.arc(x, y, Math.max((1 - p) * strokeWidth * 2.5, 0.5), 0, TAU)
      ctx.globalAlpha = (1 - p) * 0.9
      ctx.fill()
    })
  }
}
